import { useState, useEffect } from "react";
import { useAppTheme, ACCENT, useIsMobile } from "./ThemeContext";
import { useAuth } from "./AuthContext";
import { DUP_FIELD_LABELS } from "./BetaDashboard";

const API = "/api";

// Record dates come as dd.mm.yyyy; the date inputs give yyyy-mm-dd.
const isoOf = d => (d && d.length >= 10 ? `${d.slice(6, 10)}-${d.slice(3, 5)}-${d.slice(0, 2)}` : "");
const inRange = (d, from, to) => { const x = isoOf(d); return (!from || x >= from) && (!to || x <= to); };

// Light flags on single waybills (e.g. stuck in "Created"): worth a look, not a duplicate between two records.
function LightFlags({ t, mobile, dateFrom, dateTo, origin, isAdmin }) {
  const [flags, setFlags] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    fetch(`${API}/record-flags`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : { flags: [] }))
      .then(d => setFlags(d.flags || []))
      .catch(() => {});
  }, []);
  const dismiss = async id => {
    const r = await fetch(`${API}/record-flags/${id}/dismiss`, { method: "POST", credentials: "include" });
    if (r.ok) setFlags(prev => prev.filter(f => f.flag_id !== id));
  };
  const listed = flags.filter(f => inRange(f.date, dateFrom, dateTo) && (!origin || (f.origin || "").trim() === origin));
  if (!listed.length) return null;
  const tons = listed.reduce((s, f) => s + (f.unit === "t" ? f.qty || 0 : 0), 0);
  return (
    <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderLeft: "4px solid #d69e2e", borderRadius: 12, marginBottom: 16 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        all: "unset", boxSizing: "border-box", cursor: "pointer", width: "100%", padding: mobile ? "12px 14px" : "14px 20px",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
      }}>
        <span>
          <span style={{ fontWeight: 700, color: t.text, fontSize: mobile ? 13 : 14 }}>
            <span style={{ color: "#d69e2e" }}>⚑</span> Kerged lipud ({listed.length} · {Math.round(tons).toLocaleString("et-EE")} t)
          </span>
          <span style={{ display: "block", fontSize: mobile ? 11 : 12, color: t.muted, marginTop: 2 }}>
            Üksikud veoselehed, mida tasub üle kontrollida. Need on koguste sees, kuni lipp eemaldatakse.
          </span>
        </span>
        <span style={{ color: t.muted, fontSize: 12 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ padding: mobile ? "0 14px 12px" : "0 20px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
          {listed.map(f => (
            <div key={f.flag_id} style={{ fontSize: mobile ? 11.5 : 12.5, color: t.text, borderTop: `1px solid ${t.border}`, paddingTop: 6,
              display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
              <div style={{ minWidth: 0 }}>
                <span style={{ color: t.muted }}>{f.date} {f.time} · </span>
                <a href={`${API}/waybiller-deliveries/${f.record_id}/pdf`} download title="Veoselehe PDF"
                  style={{ color: ACCENT, fontWeight: 600 }}>{f.waybill_number}</a>
                <span style={{ color: t.muted }}> · </span><b>{f.reg}</b> · {f.qty} {f.unit} · {f.material}
                <div style={{ fontSize: mobile ? 10.5 : 11, color: t.mutedLight }}>{f.origin} · {f.company} · olek {f.status}</div>
                <div style={{ fontSize: mobile ? 10.5 : 11, color: t.muted }}>{f.reason}</div>
              </div>
              {isAdmin && (
                <button onClick={() => dismiss(f.flag_id)} title="Eemalda lipp" style={{
                  all: "unset", cursor: "pointer", fontSize: 11, color: t.muted, border: `1px solid ${t.border}`,
                  borderRadius: 6, padding: "4px 8px", whiteSpace: "nowrap", flexShrink: 0,
                }}>Eemalda</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SourceChanges({ t, mobile, dateFrom, dateTo }) {
  const [changes, setChanges] = useState([]);
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(null);
  useEffect(() => {
    fetch(`${API}/source-changes`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : { changes: [] }))
      .then(d => setChanges(d.changes || []))
      .catch(() => {});
  }, []);
  const listed = changes.filter(c => inRange(c.changed_at, dateFrom, dateTo));
  if (!listed.length) return null;
  return (
    <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderLeft: "4px solid #dd6b20", borderRadius: 12, marginBottom: 16 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        all: "unset", boxSizing: "border-box", cursor: "pointer", width: "100%", padding: mobile ? "12px 14px" : "14px 20px",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
      }}>
        <span>
          <span style={{ fontWeight: 700, color: t.text, fontSize: mobile ? 13 : 14 }}>Allikasüsteemides hiljem muudetud kirjed ({listed.length})</span>
          <span style={{ display: "block", fontSize: mobile ? 11 : 12, color: t.muted, marginTop: 2 }}>
            Verston või Waybiller muutis juba olemasolevat kirjet. Vana väärtus on siia salvestatud.
          </span>
        </span>
        <span style={{ color: t.muted, fontSize: 12 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ padding: mobile ? "0 14px 12px" : "0 20px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
          {listed.map((c, i) => (
            <div key={i} style={{ fontSize: mobile ? 11.5 : 12.5, color: t.text, borderTop: `1px solid ${t.border}`, paddingTop: 6 }}>
              <span style={{ color: t.muted }}>{c.changed_at} · {c.source === "verston" ? "Verston" : "Waybiller"} </span>
              <button onClick={() => setShown(c)} title="Ava kirje" style={{
                all: "unset", cursor: "pointer", color: ACCENT, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 2,
                padding: mobile ? "6px 0" : 0,
              }}>{c.record_ref}</button>
              <span style={{ color: t.muted }}> · </span>
              {c.field_label}: <span style={{ textDecoration: "line-through", color: t.muted }}>{c.old_value}</span> → <b>{c.new_value}</b>
              {c.via && <div style={{ fontSize: mobile ? 10.5 : 11, color: t.mutedLight }}>{c.via}</div>}
            </div>
          ))}
        </div>
      )}
      {shown && <ChangeRecordModal t={t} mobile={mobile} change={shown} onClose={() => setShown(null)} />}
    </div>
  );
}

function ChangeRecordModal({ t, mobile, change, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const q = new URLSearchParams({ table: change.table, record_id: change.record_id, source: change.source, ref: change.record_ref });
    fetch(`${API}/source-changes/record?${q}`, { credentials: "include" })
      .then(async r => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Viga ${r.status}`);
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message));
  }, [change]);
  useEffect(() => {
    const onKey = e => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rec = data?.record;
  // Fields whose value changed are marked in the record, so the edit is visible at a glance.
  const changedFields = new Set((data?.history || []).map(h => h.field_label));
  const field = (label, value, key = label) => (
    <>
      <span style={{ color: t.muted, fontWeight: 600 }}>{label}</span>
      <span style={changedFields.has(key) ? { fontWeight: 700, color: "#dd6b20" } : undefined}>{value || "—"}</span>
    </>
  );

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Muudetud kirje" style={{
        background: t.panel, border: `1px solid ${t.border}`, borderRadius: 14, padding: mobile ? 18 : 24,
        maxWidth: 560, width: "100%", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: mobile ? 14 : 16, fontWeight: 700, color: t.text }}>
            {change.source === "verston" ? "Verston" : "Waybiller"} {change.record_ref}
          </div>
          <button onClick={onClose} aria-label="Sulge" style={{ all: "unset", cursor: "pointer", fontSize: 18, color: t.muted, padding: 4 }}>✕</button>
        </div>
        {error ? <div style={{ color: t.error, fontSize: 13 }}>{error}</div> : !rec ? <div style={{ color: t.muted, fontSize: 13 }}>Laen...</div> : (
          <>
            <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 10, padding: mobile ? 12 : 14, marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: t.muted, textTransform: "uppercase", marginBottom: 8 }}>Kirje praegu</div>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 12px", fontSize: mobile ? 12 : 13, color: t.text }}>
                {field("Kuupäev", `${rec.date} ${rec.time}`, rec.source === "verston" ? "aeg" : "kuupäev")}
                {field("Reg nr", rec.reg, "reg nr")}
                {field("Materjal", rec.material, "materjal")}
                {field("Kogus", rec.qty ? `${rec.qty} t` : "", rec.source === "verston" ? "neto" : "väljastatud kogus")}
                {field("Päritolu", rec.origin, rec.source === "verston" ? "koht" : "laadimiskoht")}
                {field("Vedaja", rec.company, "vedaja")}
                {field("Juht", rec.driver)}
                {rec.waybill_number && field("Saateleht", rec.waybill_number)}
                {rec.external_id && field("Verston ID", rec.external_id)}
              </div>
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: t.muted, textTransform: "uppercase", marginBottom: 6 }}>Muudatused</div>
            {data.history.map((h, i) => (
              <div key={i} style={{ fontSize: mobile ? 12 : 13, color: t.text, borderTop: `1px solid ${t.border}`, padding: "6px 0" }}>
                <span style={{ color: t.muted }}>{h.changed_at} · </span>
                {h.field_label}: <span style={{ textDecoration: "line-through", color: t.muted }}>{h.old_value}</span> → <b>{h.new_value}</b>
                {h.via && <span style={{ color: t.mutedLight }}> · {h.via}</span>}
              </div>
            ))}
            {data.dup_flags > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: "#dd6b20" }}>
                ⚠ Kirjel on {data.dup_flags} {data.dup_flags === 1 ? "duplikaadi lipp" : "duplikaadi lippu"} — vaata allpool olevast nimekirjast.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const isRed = (f) => f.kind === "conflict" || f.score >= 90;

function ScoreBadge({ flag, mobile }) {
  const red = isRed(flag);
  return (
    <span style={{
      color: red ? "#e53e3e" : "#dd6b20", background: red ? "#e53e3e18" : "#dd6b2018",
      fontWeight: 800, fontSize: mobile ? 12 : 13, padding: "3px 10px", borderRadius: 6, whiteSpace: "nowrap",
    }}>{flag.kind === "conflict" ? `⛔ Konflikt · ${flag.gap_minutes} min` : `⚠ ${flag.score}%`}</span>
  );
}

function FieldTags({ flag, mobile }) {
  const color = isRed(flag) ? "#e53e3e" : "#dd6b20";
  const bg = isRed(flag) ? "#e53e3e18" : "#dd6b2018";
  const fields = flag.matched_fields;
  const labels = DUP_FIELD_LABELS;
  return (
    <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
      {(fields || []).map(f => (
        <span key={f} style={{ fontSize: mobile ? 9 : 10, fontWeight: 600, background: bg, color, padding: "1px 5px", borderRadius: 3 }}>
          {labels[f] || f}
        </span>
      ))}
    </div>
  );
}

function RecordCell({ rec, t, mobile }) {
  if (!rec) return <span style={{ color: t.muted }}>—</span>;
  const ref = rec.waybill_number || rec.external_id || "";
  return (
    <div style={{ fontSize: mobile ? 10.5 : 11.5, color: t.text, lineHeight: 1.5 }}>
      <div><span style={{ color: t.muted, fontWeight: 600 }}>{rec.source === "waybiller" ? "WB" : "VR"}</span> {ref}</div>
      <div>{rec.date} {rec.time} · <span style={{ color: ACCENT, fontWeight: 600 }}>{rec.reg}</span></div>
      <div>{rec.material} · <span style={{ fontWeight: 600 }}>{rec.qty}</span></div>
      <div style={{ color: t.mutedLight }}>{rec.origin || "—"} · {rec.company || "—"}</div>
    </div>
  );
}

export default function DuplicateReport() {
  const { t } = useAppTheme();
  const { user } = useAuth();
  const mobile = useIsMobile();
  const isAdmin = user?.role === "admin";

  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [origin, setOrigin] = useState("");
  const inPeriod = f => inRange(f.record_a.date, dateFrom, dateTo) || inRange(f.record_b.date, dateFrom, dateTo);
  const originOf = r => (r.origin || "").trim();
  const fromOrigin = f => !origin || originOf(f.record_a) === origin || originOf(f.record_b) === origin;

  useEffect(() => {
    fetch(`${API}/duplicates/list`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { setFlags(d.flags || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleDismiss = async (flagId) => {
    const r = await fetch(`${API}/duplicates/${flagId}/dismiss`, { method: "POST", credentials: "include" });
    if (r.ok) setDismissed(prev => new Set([...prev, flagId]));
  };

  const visible = flags.filter(f => {
    if (dismissed.has(f.flag_id) || !inPeriod(f) || !fromOrigin(f)) return false;
    if (filter === "conflict" && f.kind !== "conflict") return false;
    if (filter === "red" && (f.kind === "conflict" || f.score < 90)) return false;
    if (filter === "orange" && (f.kind === "conflict" || f.score >= 90)) return false;
    if (filter === "cross" && f.record_a.source === f.record_b.source) return false;
    if (filter === "within" && f.record_a.source !== f.record_b.source) return false;
    if (search) {
      const s = search.toLowerCase();
      const hay = [f.record_a.reg, f.record_a.material, f.record_a.origin, f.record_a.company,
                    f.record_b.reg, f.record_b.material, f.record_b.origin, f.record_b.company]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });

  const inScope = flags.filter(f => !dismissed.has(f.flag_id) && inPeriod(f));
  // Loading places of the flags in the period, most flags first; a flag counts once even if both records share it.
  const originCounts = {};
  inScope.forEach(f => new Set([originOf(f.record_a), originOf(f.record_b)].filter(Boolean))
    .forEach(o => { originCounts[o] = (originCounts[o] || 0) + 1; }));
  const origins = Object.keys(originCounts).sort((a, b) => originCounts[b] - originCounts[a] || a.localeCompare(b, "et"));
  const active = inScope.filter(fromOrigin);
  const period = new URLSearchParams({ ...(dateFrom && { date_from: dateFrom }), ...(dateTo && { date_to: dateTo }),
    ...(origin && { origin }) });
  const pdfParams = new URLSearchParams(period);
  if (filter === "conflict") pdfParams.set("kind", "conflict");
  else if (filter === "red" || filter === "orange") pdfParams.set("kind", "duplicate");
  const qs = p => (String(p) ? `?${p}` : "");
  const conflictCount = active.filter(f => f.kind === "conflict").length;
  const redCount = active.filter(f => f.kind !== "conflict" && f.score >= 90).length;
  const orangeCount = active.filter(f => f.kind !== "conflict" && f.score < 90).length;

  const filterBtn = (key, label) => (
    <button key={key} onClick={() => setFilter(key)} style={{
      all: "unset", cursor: "pointer", fontSize: mobile ? 11 : 12, fontWeight: filter === key ? 700 : 500,
      padding: mobile ? "5px 10px" : "6px 14px", borderRadius: 8,
      background: filter === key ? ACCENT + "18" : "transparent",
      color: filter === key ? ACCENT : t.muted,
      border: `1px solid ${filter === key ? ACCENT + "44" : t.border}`,
    }}>{label}</button>
  );

  return (
    <div style={{ padding: mobile ? "16px 12px" : "24px 20px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: mobile ? 18 : 22, fontWeight: 800, color: t.text }}>Duplikaatide raport</div>
          <div style={{ fontSize: mobile ? 11 : 12, color: t.muted, marginTop: 2 }}>
            {loading ? "Laen..." : `${active.length} lippu: ${conflictCount} konflikti, ${redCount + orangeCount} duplikaati`}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <a href={`${API}/duplicates/report.pdf${qs(pdfParams)}`} download
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: mobile ? "8px 14px" : "9px 18px",
              background: "#c62828", color: "#fff", borderRadius: 8, fontSize: mobile ? 12 : 13,
              fontWeight: 700, textDecoration: "none",
            }}>
            📄 Lae PDF raport
          </a>
          <a href={`${API}/duplicates/export${qs(period)}`} download
            style={{ fontSize: mobile ? 11 : 12, color: t.muted, textDecoration: "underline" }}>
            Excel
          </a>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {filterBtn("all", `Kõik (${active.length})`)}
        {filterBtn("conflict", `⛔ Konfliktid (${conflictCount})`)}
        {filterBtn("red", `Duplikaat ≥90% (${redCount})`)}
        {filterBtn("orange", `Duplikaat 70–89% (${orangeCount})`)}
        {filterBtn("cross", "Ristviide")}
        {filterBtn("within", "Allikasisene")}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Otsi reg, materjal..."
          style={{
            background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 12px",
            fontSize: mobile ? 11 : 12, color: t.text, outline: "none", minWidth: 140, marginLeft: "auto",
          }} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16, fontSize: mobile ? 11 : 12, color: t.muted }}>
        {[["Alates", dateFrom, setDateFrom], ["Kuni", dateTo, setDateTo]].map(([label, value, set]) => (
          <label key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {label}
            <input type="date" value={value} onChange={e => set(e.target.value)} style={{
              background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 10px", minHeight: 32,
              fontSize: mobile ? 12 : 13, color: t.text, colorScheme: "light dark", fontFamily: "inherit",
            }} />
          </label>
        ))}
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Päritolu
          <select value={origin} onChange={e => setOrigin(e.target.value)} style={{
            background: t.bg, border: `1px solid ${origin ? ACCENT + "44" : t.border}`, borderRadius: 8, padding: "6px 10px",
            minHeight: 32, fontSize: mobile ? 12 : 13, color: origin ? ACCENT : t.text, fontFamily: "inherit",
            maxWidth: mobile ? 200 : 280,
          }}>
            <option value="">Kõik</option>
            {origin && !originCounts[origin] && <option value={origin}>{origin} (0)</option>}
            {origins.map(o => <option key={o} value={o}>{o} ({originCounts[o]})</option>)}
          </select>
        </label>
        {(dateFrom || dateTo || origin) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); setOrigin(""); }} style={{
            all: "unset", cursor: "pointer", color: ACCENT, fontWeight: 600, padding: "6px 4px",
          }}>Tühjenda</button>
        )}
        {(dateFrom || dateTo || origin) && <span>· PDF ja Excel kasutavad samu filtreid</span>}
      </div>

      <LightFlags t={t} mobile={mobile} dateFrom={dateFrom} dateTo={dateTo} origin={origin} isAdmin={isAdmin} />
      <SourceChanges t={t} mobile={mobile} dateFrom={dateFrom} dateTo={dateTo} />

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: t.muted }}>Laen duplikaate...</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: t.muted }}>Lipud puuduvad</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: mobile ? 8 : 10 }}>
          {visible.map(f => (
            <div key={f.flag_id} style={{
              background: t.panel, border: `1px solid ${t.border}`, borderRadius: mobile ? 10 : 12,
              borderLeft: `4px solid ${isRed(f) ? "#e53e3e" : "#dd6b20"}`,
              padding: mobile ? "12px 14px" : "16px 20px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <ScoreBadge flag={f} mobile={mobile} />
                  <FieldTags flag={f} mobile={mobile} />
                  {f.kind === "conflict" && (
                    <span style={{ fontSize: mobile ? 10.5 : 11.5, color: t.muted }}>tavaline ring ~{f.expected_minutes} min</span>
                  )}
                  <span style={{ fontSize: 10, color: t.mutedLight }}>{f.detected_at}</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <a href={`${API}/duplicates/${f.flag_id}/pdf`} download style={{ all: "unset", cursor: "pointer", fontSize: mobile ? 11 : 12, fontWeight: 600,
                    color: t.muted, padding: "4px 12px", borderRadius: 6, border: `1px solid ${t.border}`,
                    whiteSpace: "nowrap", flexShrink: 0, textDecoration: "none" }}>PDF</a>
                {isAdmin && (
                  <button onClick={() => handleDismiss(f.flag_id)} style={{
                    all: "unset", cursor: "pointer", fontSize: mobile ? 11 : 12, fontWeight: 600,
                    color: t.muted, padding: "4px 12px", borderRadius: 6, border: `1px solid ${t.border}`,
                    whiteSpace: "nowrap", flexShrink: 0,
                  }}>Tühista</button>
                )}
                </div>
              </div>
              <div style={{
                display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: mobile ? 10 : 16,
              }}>
                <div style={{ background: t.bg, borderRadius: 8, padding: mobile ? 10 : 12, border: `1px solid ${t.border}` }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", color: t.muted, textTransform: "uppercase", marginBottom: 6 }}>Kirje A</div>
                  <RecordCell rec={f.record_a} t={t} mobile={mobile} />
                </div>
                <div style={{ background: t.bg, borderRadius: 8, padding: mobile ? 10 : 12, border: `1px solid ${t.border}` }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", color: t.muted, textTransform: "uppercase", marginBottom: 6 }}>Kirje B</div>
                  <RecordCell rec={f.record_b} t={t} mobile={mobile} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
